/*
 * StreamBank Admin v38.0
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {Fragment, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {Button, Col, FormGroup, FormLabel, Row, Table} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DateUtils from "../../../../../monitoring/src/Utils/DateUtils";
import {service as OfflineDataService} from "../../../Services/OfflineDataService";
import {service as PhotoService} from "../../../Services/PhotoService";


import {ConfirmationDialog} from "../../Common/ConfirmationDialog";
import {
    SET_CURRENT_EVENT,
    SET_HIDE_PHOTO_POINT,
    SET_PHOTO_POINTS,
    SET_PHOTOS_LOADED,
    SET_PREVIOUS_EVENT,
    SET_PREVIOUS_EVENT_ID,
    SET_PROJECT,
    UPDATE_PHOTO,
} from "../../../Redux/Actions/Types/reportActionTypes";
import {
    selectHidePhotoPointsById,
    selectLastVisiblePhotoPointId,
    selectNumberOfVisiblePhotoPoints,
    selectPhotoPointsWithPhotos
} from "../../../Redux/Selectors/photoMonitoringReportSelectors";
import {config} from "../../../Config";
import {ReportSelectComponent} from "./ReportSelectComponent";
import {DateTimeFieldEditor, TextAreaEditor} from "../../../Models/Core/FieldEditors";
import {HelpIcon} from "../../Common/HelpIcon";

const datePattern = 'MMM d, yyyy';
const dateTimePattern = 'MMM d, yyyy h:mm aa';

const getPhotoSrc = (photo) => {
    if(photo) {
        return config.getApiUrl() + "/api/photos/" + photo.photoId + "/large"
    }
    return "";
};

export const PhotoMonitoringReport = (props) => {
    const dispatch = useDispatch();
    const {procedureId, procedures, projectId, project, monitoringEventId, currentEvent, previousEventId, previousEvent} = useSelector(state => state.reportState);
    const photoPoints = useSelector(state => selectPhotoPointsWithPhotos(state));
    const numberOfVisiblePhotoPoints = useSelector(state => selectNumberOfVisiblePhotoPoints(state));
    const lastVisiblePhotoPointId = useSelector(state => selectLastVisiblePhotoPointId(state));
    
    const procedure = procedures?.find( p => String(p.procedureId) === String(procedureId) );

    const [currentPhotoPoint, setCurrentPhotoPoint] = useState(null);

    let reportName = 'Photo Point Monitoring Report';
    
    useEffect(() => {
        dispatch({type: SET_PHOTOS_LOADED});
    }, []);

    useEffect(() => {
        OfflineDataService.Download(null, projectId).then((result) => {
            const photoPoints = [];
            const currentProject = result.Projects[0];

            result.CameraPoints.forEach(cameraPoint => {
                cameraPoint.PhotoPoints.forEach(photoPoint => {
                    photoPoints.push({
                        ...cameraPoint,
                        ...photoPoint,
                        PhotoPoints: undefined,
                        name: `${cameraPoint.name}${photoPoint.name}`,
                    });
                });
            });
            dispatch({type: SET_PROJECT, project: currentProject});
            dispatch({type: SET_PHOTO_POINTS, photoPoints});
        });
    }, [projectId]);

    useEffect(() => {
        if(!monitoringEventId) {
            dispatch({type: SET_CURRENT_EVENT, event: null});
            return;
        }

        OfflineDataService.Download(null, null, monitoringEventId).then((result) => {
            dispatch({type: SET_CURRENT_EVENT, event: {
                ...result.SampleEvents[0],
                Photos: result.Photos
            }});
        });
    }, [monitoringEventId]);

    useEffect(() => {
        if(!previousEventId) {
            dispatch({type: SET_PREVIOUS_EVENT, event: null});
            return;
        }

        OfflineDataService.Download(null, null, previousEventId).then((result) => {
            dispatch({type: SET_PREVIOUS_EVENT, event: {
                ...result.SampleEvents[0],
                Photos: result.Photos
            }});
        });
    }, [previousEventId]);

    // todo: make this a selector?
    const sampleEventStaffNames = [...(currentEvent?.SampleEventStaff ?? [])]
        .map(staff => (
            project?.Users?.find(user => user.userId === staff.userId)?.name
        ))
        .filter( x => !!x );

    const renderReportSummary = () => {
        return (
            <Row xs={12}>
                <Col xs={2}>
                    <FormGroup className="sb-print-hide">
                        <FormLabel>Project Name</FormLabel>
                        <div>{project?.name ?? 'N/A'}</div>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Implementation Start Date</FormLabel>
                        <div>{project?.implementationStartDate ? DateUtils.FormatDate(project?.implementationStartDate, datePattern) : 'N/A'}</div>
                    </FormGroup>
                    <FormGroup className="sb-print-show">
                        <FormLabel>Project Developer</FormLabel>
                        <div>{project?.Organization?.name}</div>
                        <div>{project?.ProgramManager?.displayName}</div>
                        <div>{project?.ProgramManager?.defaultEmail}</div>
                    </FormGroup>
                </Col>
                <Col xs={2}>
                    <FormGroup>
                        <FormLabel>HUC</FormLabel>
                        <div>{project?.Huc?.name ?? 'N/A'}{project?.Huc?.hucCode != null ? ' (' + project?.Huc?.hucCode + ')' : ''}</div>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>River</FormLabel>
                        <div>{project?.River?.name ?? 'N/A'}</div>
                    </FormGroup>
                </Col>
                <Col xs={2}>
                    <FormGroup>
                        <FormLabel>Number of Photo Points*</FormLabel>
                        <div>{numberOfVisiblePhotoPoints} of {photoPoints?.length}</div>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Date Surveyed</FormLabel>
                        <div>{currentEvent?.startDate ? DateUtils.FormatDate(currentEvent?.startDate, datePattern) : 'N/A'}</div>
                    </FormGroup>
                </Col>
                <Col xs={6}>
                    <FormGroup>
                        <FormLabel>Surveyor(s)</FormLabel>
                        <div>{sampleEventStaffNames.length ? sampleEventStaffNames.join(', ') : 'N/A'}</div>
                    </FormGroup>
                </Col>
            </Row>
        )
    };

    const renderPhotoMonitoringParameters = () => {
        return (
            <div className="sb-print-hide">
                <h3 className="table-headers">Report Parameters</h3>
                <Table striped bordered size="sm" className="sb-model-viewer">
                    <tbody>
                        <ReportSelectComponent
                            title="Comparison Event"
                            primaryKey="sampleEventId"
                            nameLogic={ (item) => item.projectPhaseName + ' - ' + item.shortName }
                            data={(procedure?.SampleEvents ?? []).filter( se => se.sampleEventId !== monitoringEventId )}
                            value={previousEventId}
                            onChange={(value) => dispatch({type: SET_PREVIOUS_EVENT_ID, previousEventId: value})}
                            placeholder="No Comparison Event"
                            emptyMessage="No monitoring events available."
                        />
                    </tbody>
                </Table>
            </div>
        )
    };

    return (
        <Fragment>
            <EditPhotoPointModal
                photoPoint={currentPhotoPoint}
                onClose={() => setCurrentPhotoPoint(null)}
                display={!!currentPhotoPoint}
            />
            <div className="photo-monitoring-report">
                <header className="sb-print-show">
                    <div className="report-title text-left">{reportName}</div>
                    <div className="report-subtitle text-right">{project?.name ?? 'N/A'}&nbsp;&nbsp;{currentEvent?.phaseName}</div>
                </header>
                <footer className="sb-print-show">
                    <div className="report-title text-left">©2023 The Freshwater Trust</div>
                    <div className="report-page"></div>
                    <div className="report-title text-right">StreamBank® Admin is owned by The Freshwater Trust</div>
                </footer>
                <div className="sb-print-show project-name">
                    {project?.name}
                </div>
                <div className="sb-print-show project-name">
                    Report Generated On: {DateUtils.GetCurrentDate(datePattern)}
                </div>
                <div className="report-summary">
                    {renderReportSummary()}
                </div>
                {renderPhotoMonitoringParameters()}
                <h3 className="mt-3">Photos</h3>
                <div className="sb-print-hide">
                    <hr className="special-hr"/>
                </div>
                {   (previousEvent || currentEvent) &&
                    <div className="camera-point-caption">
                        <Row xs={12}>
                            {
                                previousEvent ?
                                    <Fragment>
                                        <Col xs={6}>
                                            {previousEvent?.shortName}
                                        </Col>
                                        <Col xs={6}>
                                            {currentEvent?.shortName}
                                        </Col>
                                    </Fragment> :
                                    <Col xs={12}>
                                        {currentEvent?.shortName}
                                    </Col>
                            }
                        </Row>
                    </div>
                }
                <div className="camera-point-list">
                    {
                        photoPoints.map((photoPoint, key) => (
                            <PhotoPointComponent
                                key={photoPoint?.photoPointId}
                                isLastItem={String(photoPoint.photoPointId) === String(lastVisiblePhotoPointId)}
                                photoPoint={photoPoint}
                                onEdit={(photoPoint) => setCurrentPhotoPoint(photoPoint)}
                            />
                        ))
                    }
                </div>
            </div>
        </Fragment>
    );
};

const EditPhotoPointModal = (props) => {
    const dispatch = useDispatch();
    const [figureDate, setFigureDate] = useState('');
    const [figureCaption, setFigureCaption] = useState('');

    useEffect(() => {
        setFigureDate(props.photoPoint?.figureDate ?? props.photoPoint?.created);
        setFigureCaption(props.photoPoint?.figureCaption);
    }, [props.display]);

    const savePhoto = () => {
        PhotoService.UpdatePhoto({
            ...props.photoPoint,
            figureCaption,
            figureDate,
        }).then(({result}) => {
            dispatch({type: UPDATE_PHOTO, photo: result.photo});
            props.onClose();
        })
    };

    return (
        <ConfirmationDialog
            size="xl"
            Title={`Edit Figure ${props.photoPoint?.figureNumber}`}
            Body={
                <Row xs={12} className="figure-modal no-gutters">
                    <Col xs={5}>
                        <div className="photo-container">
                            <div className="photo">
                                <img src={getPhotoSrc(props.photoPoint)} key={props.photoPoint?.photoId} />
                            </div>
                        </div>
                    </Col>
                    <Col xs={7}>
                        <Table striped bordered>
                            <tbody>
                            <tr>
                                <td>Date</td>
                                <td>
                                    <DateTimeFieldEditor
                                        Value={figureDate}
                                        OnUpdate={(value) => setFigureDate(value)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Figure Caption
                                    <HelpIcon>This caption will appear below the photo in the generated report.</HelpIcon>
                                </td>
                                <td>
                                    <TextAreaEditor
                                        Value={figureCaption}
                                        OnUpdate={(value) => setFigureCaption(value)}
                                        FieldDef={{}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Photo Notes
                                    <HelpIcon>These are internal notes captured by the monitoring staff. They will not appear on the generated report.</HelpIcon>
                                </td>
                                <td>
                                    {props.photoPoint?.notes}
                                </td>
                            </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            }
            Display={props.display}
            OnClose={() => props.onClose()}
            CancelButtonMessage="Cancel"
            ConfirmationButtonMessage="Save"
            OnConfirm={() => savePhoto()}
        />
    )
};

const ViewPhotoModal = (props) => {

    return (
        <ConfirmationDialog
            className="fullscreen-photo-modal"
            size="xl"
            Title={`Figure ${props.photo?.figureNumber}`}
            Body={
                <Row xs={12} className="figure-modal no-gutters">
                    <Col xs={12} className="photo-container">
                        <div className="photo">
                            <img src={getPhotoSrc(props?.photo)} key={props?.photo?.photoId} />
                        </div>
                    </Col>
                </Row>
            }
            Display={props.display}
            OnClose={() => props.onClose()}
            CancelButtonMessage="Close"
        />
    )
};

const PhotoPointComponent = (props) => {

    const {isLastItem, onEdit, photoPoint} = props;
    const {
        photoPointId,
        compassBearing,
        bank,
        name,
        latitude,
        previousPhoto,
        photoDirection,
        currentPhoto,
        longitude
    } = photoPoint;
    
    const dispatch = useDispatch();
    
    const {currentEvent, previousEvent} = useSelector(state => state.reportState);
    const hideState = useSelector(state => selectHidePhotoPointsById(state, photoPointId)) ?? {};
    const {isCurrentHidden = false, isPreviousHidden = false} = hideState;
    const isHidden = (isCurrentHidden && isPreviousHidden);

    // if one param, applies the same value to both
    const setIsHidden = (isCurrentHidden, isPreviousHidden = null) => {
        // if only one param was specified, apply the same value to both
        isPreviousHidden ??= isCurrentHidden;
        // if no previous event, isPreviousHidden should always match isCurrentHidden
        // (so we don't show an empty page)
        isPreviousHidden = previousEvent?.sampleEventId ? isPreviousHidden : isCurrentHidden;
        dispatch({ type: SET_HIDE_PHOTO_POINT, photoPointId, isCurrentHidden: isCurrentHidden, isPreviousHidden: isPreviousHidden });
    }

    useEffect(() => {
        setIsHidden(isCurrentHidden, !previousPhoto);
    }, [previousEvent?.sampleEventId]);

    useEffect(() => {
        setIsHidden(!currentPhoto, isPreviousHidden);
    }, [currentEvent?.sampleEventId]);

    return (
            <div className={`sb-print-full-page camera-point-item` + ((isHidden || (!currentPhoto && !previousPhoto)) ? ' sb-print-hide' : '')}>
                <div className="camera-point-title">
                    <div className="title-info">
                        <span className="photo-point-name">
                            Photo Point {name}
                        </span>
                        <span>
                            {latitude}&#176;, {longitude}&#176;
                        </span>
                        <span>
                            {photoDirection} {compassBearing}&#176;
                        </span>
                        <span>
                            Bank: {bank ?? 'N/A'}
                        </span>
                    </div>
                    <Button
                        variant="link"
                        className={isHidden ? 'danger' : ''}
                        onClick={() => setIsHidden(!isHidden) }>
                        {
                            isHidden ?
                                <span>
                                    <FontAwesomeIcon icon={['fas', 'eye-slash']} />Hidden From Report
                                </span> :
                                <span>
                                    <FontAwesomeIcon icon={['fas', 'eye']} />Hide Photo Point
                                </span>
                        }
                    </Button>
                </div>
                <Row xs={12} className="no-gutters" >
                    {
                        previousEvent &&
                        <Fragment>
                            <Col xs={6} className={(isPreviousHidden || !previousEvent) && 'sb-print-hide'}>
                                <div className="sb-print-show event-title">
                                    <span>{previousEvent?.phaseName}</span>
                                </div>
                                <FigureComponent
                                    event={previousEvent}
                                    photo={previousPhoto}
                                    isHidden={isPreviousHidden}
                                    toggleHidden={() => setIsHidden(isCurrentHidden, !isPreviousHidden)}
                                    onEdit={(photoPoint) => onEdit(photoPoint)}
                                />
                            </Col>
                        </Fragment>
                    }
                    <Col xs={previousEvent ? 6 : 12} className={isCurrentHidden && 'sb-print-hide'}>
                        <div className="sb-print-show event-title">
                            <span>{currentEvent?.phaseName}</span>
                        </div>
                        <FigureComponent
                            event={currentEvent}
                            photo={currentPhoto}
                            isHidden={isCurrentHidden}
                            toggleHidden={() => setIsHidden(!isCurrentHidden, isPreviousHidden)}
                            onEdit={(photoPoint) => onEdit(photoPoint)}
                        />
                    </Col>
                </Row>
                {
                    isLastItem &&
                    <div className="last-item-note sb-print-show mt-3">
                        *Some photographs may be omitted from this report. These omissions may be a result of de-activated photo points, or photos not repeated this monitoring event as described in the monitoring protocol. Please contact report author for more information.
                    </div>
                }
            </div>
    )
};

const FigureComponent = (props) => {
    const {event, onEdit, isHidden, photo, toggleHidden} = props;
    const dispatch = useDispatch();

    const [displayedPhoto, setDisplayedPhoto] = useState(null);

    let noPhotoMessage;

    const figureDate = photo?.figureDate ?? photo?.created;
    const figureDateFormatted =  DateUtils.FormatToLocalDateTime(figureDate, dateTimePattern);

    if (!photo) {
        noPhotoMessage = 'No Photo Available';
    }

    if (!event?.phaseName) {
        noPhotoMessage = 'Comparison Event Not Selected';
    }

    if (noPhotoMessage && !isHidden) {
        return (
            <div className="photo-point-container">
                <div className="photo-point-title sb-print-hide">
                    <span/>
                    <span>
                        <Button
                            variant="link"
                            className={isHidden ? 'danger' : ''}
                            onClick={() => toggleHidden()}>
                            {
                                isHidden ?
                                    <span>
                                        <FontAwesomeIcon icon={['fas', 'eye-slash']} />Hidden From Report
                                    </span> :
                                    <span>
                                        <FontAwesomeIcon icon={['fas', 'eye']} />Hide
                                    </span>
                            }
                        </Button>
                    </span>
                </div>
                <div className="photo no-photo-message">
                    {noPhotoMessage}
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            <ViewPhotoModal
                photo={displayedPhoto}
                onClose={() => setDisplayedPhoto(null)}
                display={!!displayedPhoto}
            />
            <div className="photo-point-container">
                <div className="photo-point-title sb-print-hide">
                <span>
                    {
                        !isHidden &&
                        <span className="photo-title mr-3">
                            Figure {photo?.figureNumber}
                        </span>
                    }
                    <span className="photo-date mr-3">
                        {figureDateFormatted}
                    </span>
                </span>
                <span>
                    <Button
                        variant="link"
                        className={isHidden ? 'danger' : ''}
                        onClick={() => toggleHidden()}>
                        {
                            isHidden ?
                                <span>
                                    <FontAwesomeIcon icon={['fas', 'eye-slash']} />Hidden From Report
                                </span> :
                                <span>
                                    <FontAwesomeIcon icon={['fas', 'eye']} />Hide
                                </span>
                        }
                    </Button>
                        {
                            !isHidden &&
                            <Button
                                variant="link"
                                className="ml-2"
                                onClick={() => onEdit({...(photo)})}>
                                <FontAwesomeIcon icon={['fas', 'pencil']} />Edit
                            </Button>
                        }
                </span>
                </div>
                <div className={`photo ${isHidden ? 'hidden' : ''}`}>
                    {
                        isHidden &&
                        <div className="hidden-message">
                            Hidden From Report
                        </div>
                    }
                    {
                        photo &&
                        <Fragment>
                            {
                                !isHidden &&
                                "Loading..."
                            }
                            <img
                            src={getPhotoSrc(photo)}
                            key={photo?.photoId}
                            onClick={() => setDisplayedPhoto(photo)}
                            onLoad={() => {dispatch({type: SET_PHOTOS_LOADED});}}
                            />
                        </Fragment>
                    }
                </div>
                {
                    (!isHidden && photo?.figureCaption) &&
                    <div className="description sb-print-hide">
                        <div className="photo-caption">
                            {photo?.figureCaption}
                        </div>
                    </div>
                }
                <div className="description sb-print-show">
                    <div>
                    <span className="figure-caption-title mr-3">
                        Figure {photo?.figureNumber}
                    </span>
                        <span className="figure-caption-date mr-3">
                        {figureDateFormatted}
                    </span>
                    </div>
                    <div className="photo-caption">
                        {photo?.figureCaption}
                    </div>
                </div>
            </div>
        </Fragment>
    )
};


